import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Layout from './layout/Layout';
import api from '../../../helper/apiAdmin';
import { getServerURL } from '../../../helper/envConfig';

const serverURL = getServerURL();

const clean = (...values) => {
  const value = values.find((candidate) =>
    candidate !== undefined && candidate !== null && String(candidate).trim() !== ''
  );
  return value === undefined ? '' : String(value).trim();
};

const safeUrl = (...values) => {
  const url = clean(...values);
  return /^https?:\/\//i.test(url) ? url : '';
};

const imageUrl = (line) => {
  const direct = safeUrl(line.product_image_url, line.product_image);
  if (direct) return direct;
  const image = Array.isArray(line.product_images) ? line.product_images[0] : null;
  const file = clean(image?.file_name, image?.thumbnail);
  if (/^https?:\/\//i.test(file)) return file;
  if (line.imageUrl && line.product_id && file) {
    return `${line.imageUrl}${line.product_id}/${file}`;
  }
  return '';
};

const orderKey = (line) =>
  clean(line.bundle_id, line.order_id, line.order_display_id, line._id)
    .replace(/:(product|flight|hotel):\d+$/, '');

const groupOrders = (lines) => {
  const groups = new Map();
  (lines || []).forEach((line) => {
    const key = orderKey(line);
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        displayId: clean(line.order_display_id, key),
        createdAt: line.created_at,
        buyerAccountId: clean(line.buyer_account_id, line.buyer_id),
        buyerName: clean(line.buyer_account_name),
        buyerFirstName: clean(line.buyer_account_first_name),
        buyerLastName: clean(line.buyer_account_last_name),
        buyerEmail: clean(line.buyer_account_email, line.buyer_email),
        buyerPhone: clean(line.buyer_account_phone),
        recipientName: clean(line.fullname, line.shipping_fullname, line.shipping_address_object?.fullname),
        recipientFirstName: clean(line.buyer_first_name, line.shipping_address_object?.first_name),
        recipientLastName: clean(line.buyer_last_name, line.shipping_address_object?.last_name),
        recipientPhone: clean(line.buyer_phone, line.contact_no, line.shipping_address_object?.contact_no),
        address: clean(line.full_address, line.shipping_address_object?.full_address),
        addressLine1: clean(line.shipping_address_line1, line.shipping_address_object?.address),
        addressLine2: clean(line.shipping_address_line2, line.shipping_address_object?.address_optional),
        city: clean(line.shipping_city, line.shipping_address_object?.city),
        state: clean(line.shipping_state, line.state_name, line.shipping_address_object?.state),
        zipcode: clean(line.shipping_zipcode, line.zipcode, line.shipping_address_object?.zipcode),
        country: clean(line.shipping_country, line.country_name, line.shipping_address_object?.country),
        total: clean(line.grand_total),
        currency: clean(line.currency, 'USD'),
        lines: [],
      });
    }
    const group = groups.get(key);
    group.lines.push(line);
    group.buyerAccountId = group.buyerAccountId || clean(line.buyer_account_id, line.buyer_id);
    group.buyerName = group.buyerName || clean(line.buyer_account_name);
    group.buyerFirstName = group.buyerFirstName || clean(line.buyer_account_first_name);
    group.buyerLastName = group.buyerLastName || clean(line.buyer_account_last_name);
    group.buyerEmail = group.buyerEmail || clean(line.buyer_account_email, line.buyer_email);
    group.buyerPhone = group.buyerPhone || clean(line.buyer_account_phone);
    group.recipientName = group.recipientName || clean(line.fullname, line.shipping_address_object?.fullname);
    group.recipientFirstName = group.recipientFirstName || clean(line.buyer_first_name, line.shipping_address_object?.first_name);
    group.recipientLastName = group.recipientLastName || clean(line.buyer_last_name, line.shipping_address_object?.last_name);
    group.recipientPhone = group.recipientPhone || clean(line.buyer_phone, line.shipping_address_object?.contact_no);
    group.address = group.address || clean(line.full_address, line.shipping_address_object?.full_address);
    group.addressLine1 = group.addressLine1 || clean(line.shipping_address_line1, line.shipping_address_object?.address);
    group.addressLine2 = group.addressLine2 || clean(line.shipping_address_line2, line.shipping_address_object?.address_optional);
    group.city = group.city || clean(line.shipping_city, line.shipping_address_object?.city);
    group.state = group.state || clean(line.shipping_state, line.state_name, line.shipping_address_object?.state);
    group.zipcode = group.zipcode || clean(line.shipping_zipcode, line.zipcode, line.shipping_address_object?.zipcode);
    group.country = group.country || clean(line.shipping_country, line.country_name, line.shipping_address_object?.country);
    group.currency = group.currency || clean(line.currency, 'USD');
  });
  return markPossibleDuplicates(Array.from(groups.values()).sort(
    (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
  ));
};

const normalizedIdentity = (value) =>
  clean(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');

const lineQuantity = (line) => {
  const quantity = Number(clean(line.quantity, line.qty, 1));
  return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
};

const orderLineFingerprint = (line) => [
  clean(line.line_type, 'product'),
  clean(line.product_id, line.item, line.product_name, line.hotel_name, line.flight_route),
  clean(line.selected_color, line.product_color, line.color),
  clean(line.selected_size, line.product_size, line.size),
  lineQuantity(line),
].map((value) => normalizedIdentity(value)).join(':');

const markPossibleDuplicates = (orders) => {
  const withCounts = orders.map((order) => {
    const productLines = order.lines.filter((line) => (line.line_type || 'product') === 'product');
    const uniqueProducts = new Set(productLines.map(orderLineFingerprint));
    return {
      ...order,
      uniqueProductCount: uniqueProducts.size,
      totalUnits: order.lines.reduce((total, line) => total + lineQuantity(line), 0),
      buyerRecipientMismatch: Boolean(
        order.buyerName &&
        order.recipientName &&
        normalizedIdentity(order.buyerName) !== normalizedIdentity(order.recipientName)
      ),
    };
  });

  const candidatesBySignature = new Map();
  withCounts.forEach((order) => {
    const buyerKey = clean(order.buyerAccountId, order.buyerEmail).toLowerCase();
    if (!buyerKey) return;
    const lineSignature = order.lines.map(orderLineFingerprint).sort().join('|');
    const signature = `${buyerKey}|${clean(order.total)}|${lineSignature}`;
    if (!candidatesBySignature.has(signature)) candidatesBySignature.set(signature, []);
    candidatesBySignature.get(signature).push(order);
  });

  const duplicateByKey = new Map();
  candidatesBySignature.forEach((matchingOrders) => {
    const chronological = [...matchingOrders].sort(
      (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
    );
    let cluster = [];
    const saveCluster = () => {
      if (cluster.length < 2) return;
      const warning = {
        count: cluster.length,
        orderIds: cluster.map((order) => order.displayId),
        totalUnits: cluster.reduce((total, order) => total + order.totalUnits, 0),
        totalCharged: cluster.reduce((total, order) => total + (Number(order.total) || 0), 0),
      };
      cluster.forEach((order) => duplicateByKey.set(order.key, warning));
    };

    chronological.forEach((order) => {
      const clusterStart = cluster[0];
      const gap = clusterStart
        ? new Date(order.createdAt || 0) - new Date(clusterStart.createdAt || 0)
        : 0;
      if (clusterStart && (!Number.isFinite(gap) || gap > 15 * 60 * 1000)) {
        saveCluster();
        cluster = [];
      }
      cluster.push(order);
    });
    saveCluster();
  });

  return withCounts.map((order) => ({
    ...order,
    possibleDuplicate: duplicateByKey.get(order.key) || null,
  }));
};

const typeConfig = {
  product: { label: 'Product', icon: <span aria-hidden="true">□</span> },
  flight: { label: 'Flight', icon: <span aria-hidden="true">✈</span> },
  hotel: { label: 'Hotel', icon: <span aria-hidden="true">H</span> },
};

const lineTitle = (line) => {
  if (line.line_type === 'flight') return clean(line.flight_airline, 'Flight');
  if (line.line_type === 'hotel') return clean(line.hotel_name, 'Hotel');
  return clean(line.item, line.product_name, 'Product');
};

const lineDetails = (line) => {
  if (line.line_type === 'flight') {
    return [
      { label: 'Trip', value: clean(line.flight_type).replace(/_/g, ' ') },
      { label: 'Route', value: clean(line.flight_route) },
      { label: 'Airline', value: clean(line.flight_airline) },
      { label: 'Flight number', value: clean(line.flight_number) },
      { label: 'Outbound departure', value: clean(line.flight_outbound_departure) },
      { label: 'Outbound arrival', value: clean(line.flight_outbound_arrival) },
      { label: 'Return date', value: clean(line.flight_return_date) },
      { label: 'Return departure', value: clean(line.flight_return_departure) },
      { label: 'Return arrival', value: clean(line.flight_return_arrival) },
      { label: 'Passenger', value: clean(line.passenger_name) },
    ].filter((detail) => detail.value);
  }
  if (line.line_type === 'hotel') {
    return [
      { label: 'Hotel', value: clean(line.hotel_name) },
      { label: 'City', value: clean(line.hotel_city) },
      { label: 'Room type', value: clean(line.room) },
      { label: 'Number of rooms', value: clean(line.hotel_rooms) },
      { label: 'Check-in', value: clean(line.check_in) },
      { label: 'Check-out', value: clean(line.check_out) },
      { label: 'Nights', value: clean(line.nights) },
      { label: 'Guests', value: clean(line.guests) },
      { label: 'Confirmation', value: clean(line.hotel_confirmation_number) },
    ].filter((detail) => detail.value);
  }
  return [
    { label: 'Color', value: clean(line.selected_color, line.product_color, line.color) },
    { label: 'Size', value: clean(line.selected_size, line.product_size, line.size) },
    { label: 'Quantity', value: clean(line.quantity, line.qty, 1) },
  ].filter((detail) => detail.value);
};

const copyText = async (value) => {
  if (!value) return;
  await navigator.clipboard.writeText(String(value));
};

function LabeledValue({ label, value, fallback = 'Not provided' }) {
  return (
    <Typography variant="body2">
      <Box component="span" fontWeight={700}>{label}:</Box> {value || fallback}
    </Typography>
  );
}

function FulfillmentLine({ line }) {
  const config = typeConfig[line.line_type] || typeConfig.product;
  const picture = imageUrl(line);
  const sourceUrl = safeUrl(
    line.worker_link,
    line.product_purchase_url,
    line.product_url,
    line.hotel_booking_link,
    line.flight_booking_link
  );
  const lensUrl = picture
    ? `https://lens.google.com/uploadbyurl?url=${encodeURIComponent(picture)}`
    : '';

  return (
    <Box sx={{ py: 2, breakInside: 'avoid' }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={3} md={2}>
          <Box
            sx={{
              width: '100%',
              aspectRatio: '1 / 1',
              bgcolor: 'grey.100',
              borderRadius: 2,
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {picture ? (
              <Box
                component="img"
                src={picture}
                alt={lineTitle(line)}
                sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            ) : (
              config.icon
            )}
          </Box>
        </Grid>
        <Grid item xs={12} sm={9} md={6}>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Chip icon={config.icon} label={config.label} size="small" />
              <Typography variant="h6">{lineTitle(line)}</Typography>
            </Stack>
            {lineDetails(line).map((detail) => (
              <Typography key={`${detail.label}-${detail.value}`} variant="body1">
                <Box component="span" fontWeight={700}>{detail.label}:</Box> {detail.value}
              </Typography>
            ))}
            {line.merchant && (
              <Typography variant="body2" color="text.secondary">
                Internal source: {line.merchant}
              </Typography>
            )}
            {line.worker_notes && (
              <Alert severity="warning" sx={{ py: 0 }}>
                {line.worker_notes}
              </Alert>
            )}
          </Stack>
        </Grid>
        <Grid item xs={12} md={4}>
          <Stack spacing={1}>
            {lensUrl && line.line_type === 'product' && (
              <Button
                variant="contained"
                href={lensUrl}
                target="_blank"
                rel="noreferrer"
              >
                Find by image
              </Button>
            )}
            {sourceUrl && (
              <Button
                variant="outlined"
                href={sourceUrl}
                target="_blank"
                rel="noreferrer"
              >
                Open source
              </Button>
            )}
            <Button
              variant="text"
              onClick={() => copyText([
                lineTitle(line),
                ...lineDetails(line).map((detail) => `${detail.label}: ${detail.value}`),
              ].join('\n'))}
            >
              Copy item
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}

export default function FulfillmentBoard({ embedded = false }) {
  const [orders, setOrders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [type, setType] = React.useState('all');
  const [selectedOrderKeys, setSelectedOrderKeys] = React.useState([]);

  const loadOrders = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.postWithToken(
        `${serverURL}api/admin/fulfillment-orders?page=1&limit=200&paid_only=1`,
        {}
      );
      setOrders(groupOrders(response.data?.orders || []));
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to load fulfillment orders.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const visibleOrders = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    return orders
      .map((order) => ({
        ...order,
        lines: type === 'all'
          ? order.lines
          : order.lines.filter((line) => (line.line_type || 'product') === type),
      }))
      .filter((order) => order.lines.length > 0)
      .filter((order) => {
        if (!query) return true;
        return [
          order.displayId,
          order.buyerName,
          order.buyerFirstName,
          order.buyerLastName,
          order.buyerEmail,
          order.buyerPhone,
          order.recipientName,
          order.recipientFirstName,
          order.recipientLastName,
          order.recipientPhone,
          order.address,
          order.city,
          order.state,
          order.zipcode,
          order.country,
          ...order.lines.flatMap((line) => [
            lineTitle(line),
            line.selected_color,
            line.selected_size,
            line.merchant,
          ]),
        ].some((value) => clean(value).toLowerCase().includes(query));
      });
  }, [orders, search, type]);

  const lineCount = visibleOrders.reduce((total, order) => total + order.lines.length, 0);
  const selectedKeySet = React.useMemo(
    () => new Set(selectedOrderKeys),
    [selectedOrderKeys]
  );
  const selectedVisibleCount = visibleOrders.filter((order) => selectedKeySet.has(order.key)).length;

  const toggleOrderSelection = (key) => {
    setSelectedOrderKeys((current) =>
      current.includes(key)
        ? current.filter((selectedKey) => selectedKey !== key)
        : [...current, key]
    );
  };

  const selectAllVisible = () => {
    setSelectedOrderKeys((current) =>
      Array.from(new Set([...current, ...visibleOrders.map((order) => order.key)]))
    );
  };

  const clearSelection = () => setSelectedOrderKeys([]);

  const exportSelectedOrders = () => {
    if (selectedVisibleCount === 0) return;
    window.print();
  };

  const content = (
    <>
      <style>{`
        @media print {
          .fulfillment-controls, .orders-workspace-controls, .sidebar, header { display: none !important; }
          .layout-main { margin: 0 !important; width: 100% !important; }
          .fulfillment-order { break-inside: avoid; box-shadow: none !important; }
          .fulfillment-order.print-excluded { display: none !important; }
        }
      `}</style>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          gap={2}
        >
          <Box>
            <Typography variant="h4" fontWeight={800}>Fulfillment</Typography>
            <Typography color="text.secondary">
              Paid orders only · Products, dupes, flights, and hotels
            </Typography>
          </Box>
          <Button
            className="fulfillment-controls"
            variant="outlined"
            disabled={selectedVisibleCount === 0}
            onClick={exportSelectedOrders}
          >
            Export Selected / Save PDF ({selectedVisibleCount})
          </Button>
        </Stack>

        <Paper className="fulfillment-controls" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Order number, customer, item, color, size..."
              inputProps={{ 'aria-label': 'Search fulfillment orders' }}
            />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {[
                ['all', 'All'],
                ['product', 'Products'],
                ['flight', 'Flights'],
                ['hotel', 'Hotels'],
              ].map(([value, label]) => (
                <Chip
                  key={value}
                  label={label}
                  color={type === value ? 'primary' : 'default'}
                  variant={type === value ? 'filled' : 'outlined'}
                  onClick={() => setType(value)}
                />
              ))}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Button size="small" variant="outlined" onClick={selectAllVisible}>
                Select all visible
              </Button>
              <Button size="small" onClick={clearSelection} disabled={selectedOrderKeys.length === 0}>
                Clear selection
              </Button>
              <Typography variant="body2" color="text.secondary">
                {selectedVisibleCount} selected in this view
              </Typography>
            </Stack>
          </Stack>
        </Paper>

        {!loading && !error && (
          <Typography color="text.secondary">
            {visibleOrders.length} grouped orders · {lineCount} items
          </Typography>
        )}

        {loading && (
          <Stack alignItems="center" py={8}><CircularProgress /></Stack>
        )}
        {error && (
          <Alert
            severity="error"
            action={<Button color="inherit" onClick={loadOrders}>Retry</Button>}
          >
            {error}
          </Alert>
        )}
        {!loading && !error && visibleOrders.length === 0 && (
          <Alert severity="info">No paid orders match these filters.</Alert>
        )}

        {visibleOrders.map((order) => {
          const isSelected = selectedKeySet.has(order.key);
          return (
          <Card
            key={order.key}
            className={`fulfillment-order${isSelected ? '' : ' print-excluded'}`}
            variant="outlined"
          >
            <CardContent>
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  gap={1}
                >
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Checkbox
                      className="fulfillment-controls"
                      checked={isSelected}
                      onChange={() => toggleOrderSelection(order.key)}
                      inputProps={{ 'aria-label': `Select order ${order.displayId}` }}
                    />
                    <Typography variant="h5" fontWeight={800}>#{order.displayId}</Typography>
                    <Chip label="Paid" color="success" size="small" />
                    {order.uniqueProductCount > 0 && (
                      <Chip
                        label={`${order.uniqueProductCount} unique product${order.uniqueProductCount === 1 ? '' : 's'}`}
                        size="small"
                      />
                    )}
                    <Chip
                      label={`${order.totalUnits} total unit${order.totalUnits === 1 ? '' : 's'}`}
                      size="small"
                    />
                  </Stack>
                  <Typography color="text.secondary">
                    {order.createdAt ? new Date(order.createdAt).toLocaleString() : ''}
                  </Typography>
                </Stack>

                {order.possibleDuplicate && (
                  <Alert
                    severity="info"
                    icon={false}
                    sx={{ bgcolor: 'grey.100', color: 'text.primary', border: '1px solid', borderColor: 'grey.300' }}
                  >
                    <Typography fontWeight={800}>Quantity check before buying</Typography>
                    <Typography variant="body2">
                      This customer placed {order.possibleDuplicate.count} separate similar paid orders within 15 minutes:
                      {' '}{order.possibleDuplicate.orderIds.map((id) => `#${id}`).join(', ')}.
                      {' '}{order.possibleDuplicate.totalUnits} total units · {order.currency}{' '}
                      {order.possibleDuplicate.totalCharged.toFixed(2)} total charged.
                      Confirm how many units the customer wants before buying. ClubMall has not changed any order.
                    </Typography>
                  </Alert>
                )}

                {order.buyerRecipientMismatch && (
                  <Alert
                    severity="info"
                    icon={false}
                    sx={{ bgcolor: 'grey.100', color: 'text.primary', border: '1px solid', borderColor: 'grey.300' }}
                  >
                    <Typography fontWeight={800}>Shipping name check</Typography>
                    Buyer and shipping recipient names are different. This can be normal for a gift, family member, or forwarding address. Check the shipping name before buying.
                  </Alert>
                )}

                <Grid container spacing={1}>
                  <Grid item xs={12} md={5}>
                    <Stack spacing={0.5} sx={{ p: 1.5, bgcolor: 'info.50', borderRadius: 2, height: '100%' }}>
                      <Typography fontWeight={800} color="info.dark">Buyer / account</Typography>
                      <LabeledValue label="Name" value={order.buyerName} />
                      <LabeledValue label="Email" value={order.buyerEmail} />
                      <LabeledValue label="Account phone" value={order.buyerPhone} />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                      sx={{ p: 1.5, bgcolor: 'success.50', borderRadius: 2, height: '100%' }}
                    >
                      <Stack spacing={0.5} sx={{ flex: 1 }}>
                        <Typography fontWeight={800} color="success.dark">Ship to / recipient</Typography>
                        {order.recipientFirstName || order.recipientLastName ? (
                          <>
                            <LabeledValue label="First name" value={order.recipientFirstName} />
                            <LabeledValue label="Last name" value={order.recipientLastName} />
                          </>
                        ) : (
                          <LabeledValue label="Recipient name" value={order.recipientName} />
                        )}
                        <LabeledValue label="Shipping phone" value={order.recipientPhone} />
                        <LabeledValue label="Address" value={order.addressLine1} />
                        {order.addressLine2 && <LabeledValue label="Address line 2" value={order.addressLine2} />}
                        <LabeledValue label="City" value={order.city} />
                        <LabeledValue label="State" value={order.state} />
                        <LabeledValue label="ZIP code" value={order.zipcode} />
                        <LabeledValue label="Country" value={order.country} />
                      </Stack>
                      {order.address && (
                        <Button
                          size="small"
                          onClick={() => copyText(order.address)}
                        >
                          Copy
                        </Button>
                      )}
                    </Stack>
                  </Grid>
                </Grid>

                <Divider />
                {order.lines.map((line, index) => (
                  <React.Fragment key={clean(line._id, `${order.key}-${index}`)}>
                    {index > 0 && <Divider />}
                    <FulfillmentLine line={line} />
                  </React.Fragment>
                ))}
              </Stack>
            </CardContent>
          </Card>
          );
        })}
      </Stack>
    </>
  );

  return embedded ? content : <Layout>{content}</Layout>;
}
